/**
 * Phase 9.1 — ProductDetailModal.
 *
 * Fullscreen-ish dialog that renders the response of
 * `GET /api/products/{sku}/drill-down`. Order IDs inside the movements
 * table open the OrderDetailModal on top (nested), and canonical_name
 * navigates to /employees/{id} closing the modal.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { api } from "@/api/client";
import { useDrillDown } from "./DrillDownContext";
import { ClickableEmployee, ClickableOrder } from "./Clickables";

const fmt = (n) => (n ?? 0).toLocaleString("es-MX");
const fmtDate = (iso) => (iso ? iso.slice(0, 10) : "—");

export default function ProductDetailModal() {
  const { productSku, closeProductModal, openOrderModal } = useDrillDown();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!productSku) {
      setData(null);
      setErr(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErr(null);
    api
      .get(`/products/${encodeURIComponent(productSku)}/drill-down`)
      .then(({ data }) => {
        if (!cancelled) setData(data);
      })
      .catch((e) => {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [productSku]);

  if (!productSku) return null;
  const p = data?.product;
  const k = data?.kpis;

  return (
    <Dialog open={!!productSku} onOpenChange={(v) => !v && closeProductModal()}>
      <DialogContent
        data-testid="product-detail-modal"
        className="max-w-5xl gap-0 p-0 overflow-hidden max-h-[92vh]"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b bg-muted/40 px-6 py-4">
          <div className="flex items-start gap-4">
            {p?.image_url ? (
              <img
                src={p.image_url}
                alt={p.sku}
                className="h-16 w-16 rounded-md object-cover ring-1 ring-border"
              />
            ) : (
              <div className="grid h-16 w-16 place-items-center rounded-md bg-slate-800 text-xs text-slate-400 ring-1 ring-border">
                SKU
              </div>
            )}
            <div>
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-lg font-bold" data-testid="product-sku">
                  {productSku}
                </span>
                {p?.vendor ? (
                  <Badge variant="outline" className="uppercase tracking-wide">
                    {p.vendor}
                  </Badge>
                ) : null}
              </div>
              <p className="mt-0.5 max-w-xl text-sm text-muted-foreground">
                {p?.description || "—"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={closeProductModal}
            className="rounded p-1 opacity-70 hover:bg-muted hover:opacity-100"
            data-testid="product-modal-close"
            aria-label="Cerrar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="max-h-[75vh] overflow-y-auto px-6 py-4 space-y-5">
          {loading && <p className="py-8 text-center text-sm text-muted-foreground">Cargando…</p>}
          {err && (
            <div className="rounded-md border border-red-800/40 bg-red-950/30 px-4 py-3 text-sm text-red-200">
              {err}
            </div>
          )}
          {!loading && data && (
            <>
              {/* KPIs */}
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <Kpi label="Movimientos" value={fmt(k.total_movements)} />
                <Kpi label="Qty Entradas" value={fmt(k.total_qty_in)} />
                <Kpi label="Qty Salidas" value={fmt(k.total_qty_out)} />
                <Kpi label="Órdenes" value={fmt(k.orders_involved)} />
              </div>

              {/* Movements */}
              <Section title="Movimientos recientes" testid="product-movements">
                {data.recent_movements.length === 0 ? (
                  <p className="py-4 text-sm text-muted-foreground">Sin movimientos.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="pb-2">Fecha</th>
                        <th className="pb-2">Tipo</th>
                        <th className="pb-2">Usuario</th>
                        <th className="pb-2 text-right">Qty</th>
                        <th className="pb-2">Folio</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border/40">
                      {data.recent_movements.map((m, i) => (
                        <tr key={i} className="hover:bg-muted/40">
                          <td className="py-1.5 tabular-nums">{fmtDate(m.timestamp)}</td>
                          <td className="py-1.5">
                            <Badge variant="secondary" className="font-mono text-[10px]">
                              {m.movement_canonical_type || m.movement_raw_type || "—"}
                            </Badge>
                          </td>
                          <td className="py-1.5">
                            {m.canonical_id ? (
                              <ClickableEmployee canonicalId={m.canonical_id}>
                                {m.canonical_name || "—"}
                              </ClickableEmployee>
                            ) : (
                              m.canonical_name || "—"
                            )}
                          </td>
                          <td className="py-1.5 text-right tabular-nums">{fmt(m.qty)}</td>
                          <td className="py-1.5">
                            {m.folio ? (
                              <ClickableOrder
                                orderId={m.folio}
                                onOpen={openOrderModal}
                              >
                                {m.folio}
                              </ClickableOrder>
                            ) : (
                              "—"
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Section>

              {/* Top users */}
              <Section title="Top usuarios manejando este SKU">
                {data.top_users_handling.length === 0 ? (
                  <p className="py-2 text-sm text-muted-foreground">—</p>
                ) : (
                  <ul className="space-y-1 text-sm">
                    {data.top_users_handling.map((u, i) => (
                      <li key={u.canonical_id + i} className="flex items-center justify-between rounded px-2 py-1 hover:bg-muted/40">
                        <ClickableEmployee canonicalId={u.canonical_id}>
                          {u.canonical_name || u.canonical_id}
                        </ClickableEmployee>
                        <span className="tabular-nums text-muted-foreground">{fmt(u.count)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>

              {/* Top orders */}
              <Section title="Top órdenes involucrando este SKU">
                {data.top_orders.length === 0 ? (
                  <p className="py-2 text-sm text-muted-foreground">—</p>
                ) : (
                  <ul className="space-y-1 text-sm">
                    {data.top_orders.map((o, i) => (
                      <li key={o.order_id + i} className="flex items-center justify-between rounded px-2 py-1 hover:bg-muted/40">
                        <ClickableOrder orderId={o.order_id} onOpen={openOrderModal}>
                          {o.order_id}
                        </ClickableOrder>
                        <span className="text-xs text-muted-foreground">
                          {fmtDate(o.last_activity)} · qty {fmt(o.qty)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Section>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Kpi({ label, value }) {
  return (
    <Card className="border-border/60 bg-muted/40 p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-xl font-semibold tabular-nums">{value}</div>
    </Card>
  );
}

function Section({ title, testid, children }) {
  return (
    <div data-testid={testid}>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      {children}
    </div>
  );
}
